import { useReadContract } from "wagmi";
import useDappChain from "./useDappChain";
import useDappConfig from "./useDappConfig";
import { formatUnits } from "viem";
import { useSelector } from "react-redux";
import { ETH } from "@config/contractsConfig";
import useBufferPool from "./useBufferPool";

const useUnstakeInfo = () => {
  const { chain, isEthereumChain } = useDappChain();
  const { config, isRestakeSupported } = useDappConfig();
  const { selectedERC20 } = useSelector((state: any) => state.user);
  const { bufferPool } = useBufferPool();

  const assetToWithdrawInto =
    selectedERC20 !== ETH ? config.tokens[selectedERC20] : config.nativeToken;

  const { data: withdrawalableAmount } = useReadContract({
    ...config.interactionPools.lrtWithdrawalManager,
    functionName: "getAvailableAssetAmount",
    args: [assetToWithdrawInto?.address],
    query: {
      enabled:
        chain && isRestakeSupported && isEthereumChain && !!assetToWithdrawInto,
    },
  });

  const { data: minAmountToWithdraw } = useReadContract({
    ...config.interactionPools.lrtWithdrawalManager,
    functionName: "minRsEthAmountToWithdraw",
    args: [assetToWithdrawInto?.address],
    query: {
      enabled:
        chain && isRestakeSupported && isEthereumChain && !!assetToWithdrawInto,
    },
  });

  const {
    data: isInstantWithdrawalEnabled,
    isPending: isInstantWithdrawalPending,
  } = useReadContract({
    ...config.interactionPools.lrtWithdrawalManager,
    functionName: "isInstantWithdrawalEnabled",
    args: [assetToWithdrawInto?.address],
    query: {
      enabled:
        chain && isRestakeSupported && isEthereumChain && !!assetToWithdrawInto,
    },
  });

  const { data: instantWithdrawalFee } = useReadContract({
    ...config.interactionPools.lrtWithdrawalManager,
    functionName: "instantWithdrawalFee",
    query: {
      enabled:
        chain && isRestakeSupported && isEthereumChain && !!assetToWithdrawInto,
    },
  });

  // Return data - instant withdrawal info is based on buffer pool availability
  return {
    withdrawalableAmount: withdrawalableAmount
      ? formatUnits(withdrawalableAmount as bigint, 18)
      : undefined,
    minAmountToWithdraw: minAmountToWithdraw
      ? formatUnits(minAmountToWithdraw as bigint, 18)
      : "0",
    bufferPoolForAsset: bufferPool[assetToWithdrawInto?.symbol] || 0,
    isInstantWithdrawalEnabled: isInstantWithdrawalEnabled === true,
    isInstantWithdrawalPending,
    instantWithdrawalFee: instantWithdrawalFee
      ? Number(instantWithdrawalFee) / 100 // Convert basis points to percentage
      : 0,
  };
};

export default useUnstakeInfo;
